import type { ReaderContent } from '@/types/reader'

const STORAGE_KEY = 'miaozi:reader-progress'
export const READER_PROGRESS_CHANGED_EVENT = 'miaozi:reader-progress-changed'

export type ReaderProgress = {
  blockId: string
  blockIndex: number
  percent: number
  updatedAt: string
}

export type LocalReaderProgressRecord = ReaderProgress & {
  contentId: string
  title: string
  sourceType: ReaderContent['sourceType']
  href: string
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

export function createReaderProgress(
  content: Pick<ReaderContent, 'blocks'>,
  blockIndex: number,
  now = new Date()
): ReaderProgress | null {
  const block = content.blocks[blockIndex]
  if (!block) return null

  return {
    blockId: block.id,
    blockIndex,
    percent:
      content.blocks.length > 0
        ? Math.round(((blockIndex + 1) / content.blocks.length) * 100)
        : 0,
    updatedAt: now.toISOString()
  }
}

export function normalizeReaderProgress(
  content: Pick<ReaderContent, 'blocks'>,
  value: unknown
): ReaderProgress | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Partial<ReaderProgress>

  const candidateIndex =
    typeof record.blockIndex === 'number' && Number.isFinite(record.blockIndex)
      ? Math.trunc(record.blockIndex)
      : -1
  const blockIndex =
    candidateIndex >= 0 && candidateIndex < content.blocks.length
      ? candidateIndex
      : typeof record.blockId === 'string'
        ? content.blocks.findIndex((block) => block.id === record.blockId)
        : -1

  if (blockIndex < 0 || blockIndex >= content.blocks.length) return null

  const block = content.blocks[blockIndex]
  const percent =
    typeof record.percent === 'number' && Number.isFinite(record.percent)
      ? clamp(Math.round(record.percent), 0, 100)
      : Math.round(((blockIndex + 1) / content.blocks.length) * 100)

  return {
    blockId: block.id,
    blockIndex,
    percent,
    updatedAt:
      typeof record.updatedAt === 'string' && record.updatedAt
        ? record.updatedAt
        : new Date(0).toISOString()
  }
}

function readAllProgress(): LocalReaderProgressRecord[] {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeAllProgress(records: LocalReaderProgressRecord[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(0, 20)))
  window.dispatchEvent(new Event(READER_PROGRESS_CHANGED_EVENT))
}

export function saveLocalReaderProgress(
  content: ReaderContent,
  href: string,
  progress: ReaderProgress
) {
  const nextRecord: LocalReaderProgressRecord = {
    ...progress,
    contentId: content.id,
    title: content.title,
    sourceType: content.sourceType,
    href
  }

  const records = readAllProgress().filter(
    (record) => record.contentId !== content.id
  )
  writeAllProgress(
    [nextRecord, ...records].sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  )
}

export function readLocalReaderProgress(
  content: Pick<ReaderContent, 'id' | 'blocks'>
) {
  const record = readAllProgress().find((item) => item.contentId === content.id)
  if (!record) return null
  return normalizeReaderProgress(content, record)
}

export function readRecentLocalReaderProgress() {
  return (
    readAllProgress().sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )[0] ?? null
  )
}
